import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTheme } from '@/context/ThemeContext';
import { Loader2, Check, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ConnectorCallbackPage() {
  const [params] = useSearchParams();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const done = useRef(false);
  const [status, setStatus] = useState('working');
  const [detail, setDetail] = useState('');

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const provider = sessionStorage.getItem('connector_provider') || '';
    const redirect_uri = sessionStorage.getItem('connector_redirect_uri') || `${window.location.origin}/connections/callback`;
    sessionStorage.removeItem('connector_provider');
    sessionStorage.removeItem('connector_redirect_uri');
    const code = params.get('code');
    const state = params.get('state');
    const error = params.get('error') || params.get('error_description');

    if (error || !code || !state || !provider) {
      setStatus('error');
      setDetail(error || 'The platform did not return an authorization code.');
      setTimeout(() => navigate('/connections'), 3500);
      return;
    }
    axios.post(`${API}/api/connectors/${provider}/exchange`, { code, state, redirect_uri }, { withCredentials: true })
      .then(() => { setStatus('done'); navigate(`/connections?picker=${provider}`, { replace: true }); })
      .catch(err => {
        const d = err?.response?.data?.detail;
        setStatus('error');
        setDetail(typeof d === 'string' ? d : d?.message || d?.error || 'Token exchange failed.');
        setTimeout(() => navigate('/connections'), 4000);
      });
  }, [params, navigate]);

  return (
    <div className={`min-h-screen flex items-center justify-center px-4 ${isDark ? 'bg-zinc-950 text-zinc-100' : 'bg-zinc-50 text-zinc-900'}`}>
      <div data-testid="connector-callback" className={`text-center p-8 rounded-2xl border max-w-md w-full ${isDark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-white'}`}>
        {status === 'working' && (
          <>
            <Loader2 size={34} className="animate-spin mx-auto mb-4" style={{ color: '#00D4FF' }} />
            <h1 className="text-base md:text-lg font-bold mb-2">Finishing the connection…</h1>
            <p className="text-sm opacity-70">Exchanging the authorization code server-side.</p>
          </>
        )}
        {status === 'done' && (
          <>
            <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: '#06D6A0' }}>
              <Check size={24} className="text-white" />
            </div>
            <h1 className="text-base md:text-lg font-bold mb-2">Connected</h1>
            <p className="text-sm opacity-70">Now choose which accounts to read.</p>
          </>
        )}
        {status === 'error' && (
          <>
            <AlertTriangle size={34} className="mx-auto mb-4" style={{ color: '#FFD166' }} />
            <h1 className="text-base md:text-lg font-bold mb-2">Connection not completed</h1>
            <p data-testid="callback-error" className="text-sm opacity-70">{detail}</p>
          </>
        )}
      </div>
    </div>
  );
}
